
import { useState, useEffect, useCallback } from 'react';
import {
    collection,
    doc,
    query,
    where,
    onSnapshot,
    getDocs,
    writeBatch,
    increment
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Favorite, Listing } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { notify } from '../lib/toast';

export const useFavorites = () => {
    const { currentUser: user } = useAuth();
    const [favorites, setFavorites] = useState<Favorite[]>([]);
    const [loading, setLoading] = useState(true);
    const [favoritesMap, setFavoritesMap] = useState<Record<string, boolean>>({});

    // Kullanıcının favorilerini dinle
    useEffect(() => {
        if (!user) {
            setFavorites([]);
            setFavoritesMap({});
            setLoading(false);
            return;
        }

        const q = query(
            collection(db, 'favorites'),
            where('userId', '==', user.id)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const favoriteList: Favorite[] = [];
            const map: Record<string, boolean> = {};

            snapshot.forEach((doc) => {
                const data = doc.data() as Omit<Favorite, 'id'>;
                favoriteList.push({
                    id: doc.id,
                    ...data,
                    createdAt: data.createdAt?.toDate?.() || new Date()
                });
                map[data.listingId] = true;
            });

            setFavorites(favoriteList);
            setFavoritesMap(map);
            setLoading(false);
        }, (error) => {
            console.error('Favorites listener error:', error);
            if (error.code === 'permission-denied') {
                // Gracefully handle permission denied (usually transient during auth)
                setFavorites([]);
                setFavoritesMap({});
            } else {
                notify.error('Favoriler yüklenirken hata oluştu');
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user]);

    // Favori ekle
    const addFavorite = useCallback(async (listingId: string, listingData?: Partial<Listing>) => {
        if (!user) {
            notify.error('Favori eklemek için giriş yapmalısınız');
            return false;
        }

        if (favoritesMap[listingId]) {
            notify.info('Bu ilan zaten favorilerinizde');
            return false;
        }

        try {
            const batch = writeBatch(db);

            // 1. Favori belgesini ekle
            const favoriteRef = doc(collection(db, 'favorites'));
            batch.set(favoriteRef, {
                userId: user.id,
                listingId,
                listingData: listingData ? {
                    title: listingData.title,
                    price: listingData.price,
                    image_urls: listingData.imageUrls?.[0], // Fixed prop name imageUrls vs image_urls
                    seller_id: listingData.sellerId
                } : null,
                createdAt: new Date()
            });

            // 2. Listing'in favoritesCount'unu artır
            const listingRef = doc(db, 'listings', listingId);
            batch.update(listingRef, {
                favorites_count: increment(1)
            });

            // 3. User'ın favoritesCount'unu artır
            const userRef = doc(db, 'users', user.id);
            batch.update(userRef, {
                favoritesCount: increment(1)
            });

            await batch.commit();
            notify.success('Favorilere eklendi');
            return true;
        } catch (error) {
            console.error('Add favorite error:', error);
            notify.error('Favori eklenirken hata oluştu');
            return false;
        }
    }, [user, favoritesMap]);

    // Favori kaldır
    const removeFavorite = useCallback(async (listingId: string) => {
        if (!user) return false;

        try {
            // Favori ID'sini bul
            const q = query(
                collection(db, 'favorites'),
                where('userId', '==', user.id),
                where('listingId', '==', listingId)
            );

            const snapshot = await getDocs(q);
            if (snapshot.empty) return false;

            const batch = writeBatch(db);

            // Tüm eşleşen favorileri sil (güvenlik için)
            snapshot.forEach((doc) => {
                batch.delete(doc.ref);
            });

            // Listing'in favoritesCount'unu azalt
            const listingRef = doc(db, 'listings', listingId);
            batch.update(listingRef, {
                favorites_count: increment(-1)
            });

            // User'ın favoritesCount'unu azalt
            const userRef = doc(db, 'users', user.id);
            batch.update(userRef, {
                favoritesCount: increment(-1)
            });

            await batch.commit();
            notify.info('Favorilerden çıkarıldı');
            return true;
        } catch (error) {
            console.error('Remove favorite error:', error);
            notify.error('Favori kaldırılırken hata oluştu');
            return false;
        }
    }, [user]);

    // Favori toggle
    const toggleFavorite = useCallback(async (listingId: string, listingData?: Partial<Listing>) => {
        if (favoritesMap[listingId]) {
            return await removeFavorite(listingId);
        } else {
            return await addFavorite(listingId, listingData);
        }
    }, [favoritesMap, addFavorite, removeFavorite]);

    // Favori mi kontrolü
    const isFavorite = useCallback((listingId: string) => {
        return !!favoritesMap[listingId];
    }, [favoritesMap]);

    // Favori ID'sini getir
    const getFavoriteId = useCallback((listingId: string) => {
        const favorite = favorites.find(f => f.listingId === listingId);
        return favorite?.id;
    }, [favorites]);

    return {
        favorites,
        favoritesCount: favorites.length,
        loading,
        addFavorite,
        removeFavorite,
        toggleFavorite,
        isFavorite,
        getFavoriteId,
        favoritesMap
    };
};
